import type { ButtonHTMLAttributes, JSX, ReactNode } from 'react';
import { Icon } from './Icon';

export type ButtonVariant = 'primary' | 'accent' | 'secondary' | 'ghost' | 'danger' | 'plain';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  readonly variant?: ButtonVariant | undefined;
  readonly size?: 'sm' | 'md' | undefined;
  readonly block?: boolean | undefined;
  readonly pill?: boolean | undefined;
  /** Icon rendered before the label. */
  readonly icon?: string | undefined;
  /** Icon rendered after the label. */
  readonly iconAfter?: string | undefined;
  readonly className?: string | undefined;
  readonly children?: ReactNode | undefined;
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: 'btn--primary',
  accent: 'btn--accent',
  secondary: 'btn--secondary',
  ghost: 'btn--ghost',
  danger: 'btn--danger',
  plain: '',
};

export function Button({
  variant = 'secondary',
  size = 'md',
  block = false,
  pill = false,
  icon,
  iconAfter,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps): JSX.Element {
  const classes = [
    'btn',
    VARIANT_CLASS[variant],
    size === 'sm' ? 'btn--sm' : '',
    block ? 'btn--block' : '',
    pill ? 'btn--pill' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button type={type} className={classes} {...rest}>
      {icon !== undefined && <Icon name={icon} />}
      {children}
      {iconAfter !== undefined && <Icon name={iconAfter} />}
    </button>
  );
}

/** Groups buttons with the standard gap and wrapping. */
export function ButtonGroup({
  children,
  className,
  style,
}: {
  readonly children: ReactNode;
  readonly className?: string | undefined;
  readonly style?: React.CSSProperties | undefined;
}): JSX.Element {
  return (
    <div className={`btn-group ${className ?? ''}`} style={style}>
      {children}
    </div>
  );
}

/** Icon-only close/action button. */
export function IconButton({
  icon,
  label,
  onClick,
  className,
}: {
  readonly icon: string;
  readonly label: string;
  readonly onClick: () => void;
  readonly className?: string | undefined;
}): JSX.Element {
  return (
    <button type="button" className={`x-btn ${className ?? ''}`} onClick={onClick} title={label}>
      <Icon name={icon} title={label} />
    </button>
  );
}
